import type { components } from './esquema'

// Os tipos vêm do OpenAPI da API, gerados por `npm run gerar-contrato`. Nada de contrato é
// escrito à mão aqui: uma segunda descrição do mesmo endpoint divergiria da primeira. É o mesmo
// contrato que o app de campo consome — as duas peças falam com a mesma API.

type E = components['schemas']

export type VisitaResumo = E['VisitaResumo']
export type QuadroResumo = E['QuadroResumo']
export type Recusa = E['Recusa']
export type FotoRecebida = E['FotoRecebida']
export type AuditorResumo = E['AuditorResumo']

/** O que a API recusou, com todas as falhas de uma vez — é assim que a tela as mostra. */
export class RecusaDaApi extends Error {
  readonly status: number
  readonly falhas: string[]

  constructor(status: number, falhas: string[], mensagem: string) {
    super(mensagem)
    this.name = 'RecusaDaApi'
    this.status = status
    this.falhas = falhas
  }
}

/** O arquivo que a API entregou, com o nome que ela mandou no cabeçalho. */
export type Baixado = { blob: Blob; nome: string }

const CHAVE_DO_TOKEN = 'smartlaudos.token'

/**
 * Avisado quando a sessão acaba. Não recebe nada: o motivo é lido de `motivoDaSaida`, e uma fonte
 * só evita a versão que chega pelo aviso divergir da que se lê depois.
 */
type OuvinteDeSessao = () => void

export class Api {
  readonly base: string
  private token: string | null = localStorage.getItem(CHAVE_DO_TOKEN)
  private readonly ouvintes = new Set<OuvinteDeSessao>()
  private motivo: string | null = null

  constructor(base: string) {
    this.base = base
  }

  get autenticado() {
    return this.token !== null
  }

  /** Por que a sessão acabou, ou nulo quando foi a pessoa que saiu. */
  get motivoDaSaida() {
    return this.motivo
  }

  private cabecalhos(opcoes: RequestInit = {}) {
    const cabecalhos = new Headers(opcoes.headers)
    if (this.token) cabecalhos.set('Authorization', `Bearer ${this.token}`)
    if (typeof opcoes.body === 'string') cabecalhos.set('Content-Type', 'application/json')
    return cabecalhos
  }

  private async responde(caminho: string, opcoes: RequestInit = {}) {
    const resposta = await fetch(this.base + caminho, {
      ...opcoes,
      headers: this.cabecalhos(opcoes),
    })

    // **Perder a sessão não é erro de tela, é troca de tela.** Limpar o token sem avisar quem
    // desenha deixaria a pessoa numa página que não carrega mais nada, sem caminho de volta que
    // não fosse recarregar na mão. O `throw` continua, para quem chamou parar onde estava.
    if (resposta.status === 401) {
      this.sair('A sessão expirou. Entre de novo.')
      throw new RecusaDaApi(401, [], 'A sessão expirou. Entre de novo.')
    }

    if (!resposta.ok) {
      const recusa = await this.leRecusa(resposta)
      throw new RecusaDaApi(resposta.status, recusa.falhas, recusa.mensagem)
    }

    return resposta
  }

  private async pede<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
    const resposta = await this.responde(caminho, opcoes)
    if (resposta.status === 204) return undefined as T
    return (await resposta.json()) as T
  }

  private async leRecusa(resposta: Response): Promise<{ mensagem: string; falhas: string[] }> {
    try {
      const corpo = (await resposta.json()) as Partial<Recusa>
      if (corpo?.falhas?.length)
        return { mensagem: corpo.mensagem ?? 'A API recusou.', falhas: corpo.falhas }
    } catch {
      // Resposta sem corpo JSON: cai na mensagem genérica abaixo.
    }
    return { mensagem: `A API respondeu ${resposta.status}.`, falhas: [] }
  }

  // ---------------------------------------------------------------- sessão
  /** A mesma identidade do app de campo: quem entra aqui é o auditor que fez a visita. */
  async entrar(email: string, senha: string) {
    const resposta = await fetch(`${this.base}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password: senha }),
    })
    if (!resposta.ok) throw new RecusaDaApi(resposta.status, [], 'E-mail ou senha não conferem.')

    const { accessToken } = (await resposta.json()) as { accessToken: string }
    this.token = accessToken
    this.motivo = null
    localStorage.setItem(CHAVE_DO_TOKEN, accessToken)
  }

  /**
   * Avisa quando a sessão acaba — pelo botão de sair ou por um 401 no meio de qualquer coisa.
   * Devolve como cancelar a inscrição, que é o que o `useEffect` espera.
   */
  aoPerderSessao(ouvinte: OuvinteDeSessao) {
    this.ouvintes.add(ouvinte)
    return () => void this.ouvintes.delete(ouvinte)
  }

  /** @param motivo o que dizer na tela de entrada. Nulo quando a pessoa mesma saiu. */
  sair(motivo: string | null = null) {
    this.motivo = motivo
    this.token = null
    localStorage.removeItem(CHAVE_DO_TOKEN)
    this.ouvintes.forEach((ouvinte) => ouvinte())
  }

  // ---------------------------------------------------------------- listar
  visitas = () => this.pede<VisitaResumo[]>('/visitas')
  visita = (id: string) => this.pede<VisitaResumo>(`/visitas/${id}`)
  quadros = (visitaId: string) => this.pede<QuadroResumo[]>(`/visitas/${visitaId}/quadros`)

  // ---------------------------------------------------------------- as fotos do quadro
  /**
   * A evidência do quadro: as três ou quatro fotos, na ordem em que foram tiradas em campo.
   *
   * **Em resolução plena, que é como elas foram guardadas.** Não há miniatura no servidor, então
   * abrir o painel de um quadro baixa alguns megabytes — é console de desktop, e a alternativa
   * seria uma segunda cópia da foto no disco.
   */
  fotosDoQuadro = (quadroId: string) => this.pede<FotoRecebida[]>(`/quadros/${quadroId}/fotos`)

  /** O conteúdo da foto. Atrás de autenticação: `<img src>` direto voltaria 401. */
  baixarFoto = (fotoId: string) => this.baixa(`/fotos/${fotoId}/conteudo`)

  // ---------------------------------------------------------------- as contas
  /**
   * Os auditores da instalação. **Não há papéis**: quem está autenticado administra contas — é a
   * decisão de "poucos auditores, sem papéis nesta fase". O que isso fecha é a porta de fora, o
   * `/auth/register` anônimo, que só vale para a primeira conta da instalação.
   */
  auditores = () => this.pede<AuditorResumo[]>('/auditores')

  criarAuditor = (email: string, senha: string) =>
    this.pede<AuditorResumo>('/auditores', {
      method: 'POST',
      body: JSON.stringify({ email, senha }),
    })

  alterarAuditor = (id: string, email: string, ativo: boolean) =>
    this.pede<AuditorResumo>(`/auditores/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ email, ativo }),
    })

  trocarSenha = (id: string, senha: string) =>
    this.pede<void>(`/auditores/${id}/senha`, {
      method: 'PUT',
      body: JSON.stringify({ senha }),
    })

  /**
   * **Só a conta que nunca assinou nada.** A que já criou visita ou fechou quadro é desativada, e a
   * API recusa apagá-la: `CriadaPor` e `FechadoPor` guardam o id de quem assinou, e apagar
   * deixaria a assinatura do laudo sem dono.
   */
  apagarAuditor = (id: string) => this.pede<void>(`/auditores/${id}`, { method: 'DELETE' })

  // ---------------------------------------------------------------- apagar
  /**
   * **Apagar não tem volta, e leva a evidência junto** — as fotos saem do banco e do disco. Não
   * existe `.xlsx` guardado: o laudo é gerado do banco a cada download, então apagar o quadro é
   * apagar o laudo dele. Quem chama confirma antes; a API não pergunta nada.
   */
  apagarQuadro = (id: string) => this.pede<void>(`/quadros/${id}`, { method: 'DELETE' })

  apagarVisita = (id: string) => this.pede<void>(`/visitas/${id}`, { method: 'DELETE' })

  // ---------------------------------------------------------------- entregar
  /**
   * O `.xlsx` de um quadro. **A API gera na hora** — não existe arquivo guardado para regerar, e é
   * por isso que baixar de novo depois de uma correção já entrega o laudo corrigido.
   *
   * O download grava data e hora no quadro, e é isso que limpa a marca de afetado.
   */
  baixarLaudo = (quadroId: string) => this.baixa(`/quadros/${quadroId}/laudo`)

  /**
   * A visita inteira num zip, ou só os quadros marcados por correção no bloco documental — que é o
   * atalho de quem precisa reenviar ao cliente e não quer procurar quadro a quadro qual mudou.
   */
  baixarLaudosDaVisita = (visitaId: string, apenasAfetados = false) =>
    this.baixa(`/visitas/${visitaId}/laudos?apenasAfetados=${apenasAfetados}`)

  /**
   * O arquivo vem atrás de autenticação, então não dá para apontar um link direto para a API: sem
   * o cabeçalho a resposta seria 401. Busca-se o corpo e o nome vem do `Content-Disposition`, que
   * é o nome que a API decidiu — repeti-lo aqui criaria uma segunda regra de nome de arquivo.
   */
  private async baixa(caminho: string): Promise<Baixado> {
    const resposta = await this.responde(caminho)
    return {
      blob: await resposta.blob(),
      nome: nomeDoCabecalho(resposta.headers.get('content-disposition')),
    }
  }
}

/** `attachment; filename=x.xlsx; filename*=UTF-8''x.xlsx` — o `filename*` manda quando existe. */
export function nomeDoCabecalho(cabecalho: string | null, padrao = 'laudo.xlsx') {
  if (!cabecalho) return padrao

  const estrela = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cabecalho)
  if (estrela) return decodeURIComponent(estrela[1].trim().replace(/^"|"$/g, ''))

  const simples = /filename="?([^";]+)"?/i.exec(cabecalho)
  return simples ? simples[1].trim() : padrao
}

export const api = new Api(
  (import.meta.env.VITE_API as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:8080',
)
